import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard, Card, Loading, EmptyState } from "@/components/primitives";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate, currentMonth, isToday } from "@/lib/format";
import { Truck, CalendarDays, Clock, Send, Plus, MessageSquare, FileText, CheckCircle2, ArrowRight, Megaphone, ShieldCheck, MapPin } from "lucide-react";

const ACTIVE = ["dispatched", "accepted", "on_scene", "transporting", "delivered"];

function QuickAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex items-center gap-3 rounded-lg border border-curtron-line bg-curtron-surface px-4 py-3.5 text-left hover:border-curtron-green hover:shadow-sm transition-all group">
      <span className="w-10 h-10 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center group-hover:bg-curtron-green group-hover:text-white transition-colors">
        <Icon className="w-5 h-5" />
      </span>
      <span className="font-medium text-curtron-ink">{label}</span>
    </button>
  );
}

export default function Dashboard() {
  const { user, isAdmin, effectiveIsAdmin } = useAuth();
  const [calls, setCalls] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/calls").then((r) => setCalls(r.data)).catch(() => setCalls([]));
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);

  if (calls === null) return <Loading />;

  const scoped = effectiveIsAdmin
    ? calls
    : calls.filter((c) => user && (c.assigned_to === user.id || c.assigned_to_secondary === user.id || c.driver_id === user.id));

  const month = currentMonth();
  const thisMonth = scoped.filter((c) => c.month === month);
  const today = scoped.filter((c) => isToday(c.scheduled_at));
  const dispatched = scoped.filter((c) => c.status === "dispatched");
  const active = scoped.filter((c) => ACTIVE.includes(c.status))
    .sort((a, b) => new Date(a.scheduled_at || a.created_at) - new Date(b.scheduled_at || b.created_at));
  const recent = [...scoped].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 8);

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-curtron-ink tracking-tight" data-testid="dashboard-welcome">
            Welcome back, {user?.name?.split(" ")[0] || "there"}
          </h1>
          {isAdmin && effectiveIsAdmin && <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide bg-curtron-green/12 text-curtron-green rounded-md px-2 py-0.5"><ShieldCheck className="w-3 h-3" /> Admin</span>}
          {isAdmin && !effectiveIsAdmin && <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide bg-curtron-ochre/15 text-curtron-ochre rounded-md px-2 py-0.5"><Truck className="w-3 h-3" /> Driver view</span>}
        </div>
        <p className="text-sm text-curtron-sub mt-1">{effectiveIsAdmin ? "Operational overview for your transport hub." : "Your assigned transports."}</p>
      </div>

      {effectiveIsAdmin && (
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <StatCard label="This month" value={thisMonth.length} icon={CalendarDays} accent="info" testid="stat-this-month" />
          <StatCard label="Today" value={today.length} icon={Clock} accent="moss" testid="stat-today" />
          <StatCard label="Dispatched" value={dispatched.length} icon={Send} accent="ochre" testid="stat-dispatched" />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-4">
        {effectiveIsAdmin && <QuickAction icon={Plus} label="New call" onClick={() => navigate("/transports/new")} testid="quick-new-call" />}
        <QuickAction icon={MessageSquare} label="Chat" onClick={() => navigate("/chat")} testid="quick-chat" />
        <QuickAction icon={FileText} label="Documents" onClick={() => navigate("/documents")} testid="quick-documents" />
      </div>

      <Card className="mt-4" data-testid="active-calls-card">
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-curtron-line">
          <h2 className="font-heading font-semibold text-curtron-ink">Active calls</h2>
          <button onClick={() => navigate("/fleet")} className="text-sm text-curtron-green font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="dashboard-view-fleet">
            Live fleet <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {active.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12 px-4" data-testid="active-empty">
            <div className="w-12 h-12 rounded-full bg-curtron-green/10 flex items-center justify-center mb-3"><CheckCircle2 className="w-6 h-6 text-curtron-green" /></div>
            <div className="font-heading font-semibold text-curtron-ink">No active calls</div>
            <div className="text-sm text-curtron-sub mt-1">Enjoy the rest.</div>
          </div>
        ) : (
          <div className="divide-y divide-curtron-line">
            {active.map((c) => (
              <button key={c.id} onClick={() => navigate(`/transports/${c.id}`)} data-testid={`active-call-${c.id}`}
                className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 text-left hover:bg-curtron-bg transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm text-curtron-ink truncate">{c.deceased_name || "—"}</div>
                  <div className="text-xs text-curtron-muted truncate flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" /> {c.agency_for || "—"} · {fmtDate(c.scheduled_at)}</div>
                </div>
                <div className="text-xs text-curtron-sub hidden sm:block truncate max-w-[140px]">{c.assigned_to_name || c.driver_name || "Unassigned"}</div>
                <StatusBadge status={c.status} />
              </button>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 mt-4">
        <Card>
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-curtron-line">
            <h2 className="font-heading font-semibold text-curtron-ink">Recent transports</h2>
            <button onClick={() => navigate("/transports")} className="text-sm text-curtron-green font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="dashboard-view-all-transports">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          {recent.length === 0 ? (
            <EmptyState icon={Truck} title="No transports yet" subtitle="Create your first transport to get started." />
          ) : (
            <div className="divide-y divide-curtron-line">
              {recent.map((c) => (
                <button key={c.id} onClick={() => navigate(`/transports/${c.id}`)} data-testid={`dashboard-transport-row-${c.id}`}
                  className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 text-left hover:bg-curtron-bg transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-curtron-ink truncate">{c.deceased_name || "—"}</div>
                    <div className="text-xs text-curtron-muted truncate">{c.agency_for || "—"} · {fmtDate(c.scheduled_at)}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-curtron-line">
            <h2 className="font-heading font-semibold text-curtron-ink flex items-center gap-2"><Megaphone className="w-4 h-4 text-curtron-green" /> Announcements</h2>
            <button onClick={() => navigate("/announcements")} className="text-sm text-curtron-green font-medium" data-testid="dashboard-view-announcements">All</button>
          </div>
          {announcements.length === 0 ? (
            <EmptyState icon={Megaphone} title="No announcements" />
          ) : (
            <div className="divide-y divide-curtron-line">
              {announcements.slice(0, 5).map((a) => (
                <div key={a.id} className="px-4 sm:px-5 py-3">
                  <div className="flex items-center gap-2">
                    {a.pinned && <span className="text-[10px] uppercase tracking-wide bg-curtron-ochre/20 text-curtron-ochre rounded px-1.5 py-0.5">Pinned</span>}
                    <div className="font-medium text-sm text-curtron-ink truncate">{a.title}</div>
                  </div>
                  <div className="text-xs text-curtron-sub mt-1 line-clamp-2">{a.body}</div>
                  <div className="text-[11px] text-curtron-muted mt-1">{a.posted_by_name} · {fmtDate(a.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
